import { useEffect } from "react";

const SITE_NAME = "HCA Moravia";
const DEFAULT_TITLE = "HCA Moravia — školení a rozvoj";
const DEFAULT_DESCRIPTION =
  "HCA Moravia — prémiový partner pro executive vzdělávání, leadership, obchodní komunikaci a rozvoj managementu. Otevřené i interní semináře, workshopy a dlouhodobé programy.";
const DESCRIPTION_MAX = 160;
/**
 * Canonical vždy míří na ostrou doménu — ne na window.location.origin.
 * Staging/preview se tak canonicalizuje na produkci místo sebe sama
 * (a robots.txt mimo produkci indexaci stejně zakazuje).
 */
const CANONICAL_BASE = "https://hcamoravia.cz";

interface PageMeta {
  /** Název stránky bez brandu — hook doplní „ — HCA Moravia". Undefined = výchozí. */
  title?: string | undefined;
  /** Popis pro meta description + og:description. Undefined = výchozí. */
  description?: string | undefined;
  /** Cesta pro canonical + og:url (např. "/terminy"). Undefined = bez canonical. */
  path?: string | undefined;
  /** True = stránka se nemá indexovat (soft-404, chybové stavy). Vynechá canonical. */
  noindex?: boolean | undefined;
}

function upsertMeta(attr: "name" | "property", key: string, content: string): void {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertCanonical(href: string): void {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

/** Ořízne popis na délku snippetu, bez useknutí uprostřed slova. */
function truncate(text: string): string {
  const clean = text.replace(/\s+/g, " ").trim();
  if (clean.length <= DESCRIPTION_MAX) return clean;
  const cut = clean.slice(0, DESCRIPTION_MAX);
  return `${cut.slice(0, cut.lastIndexOf(" "))}…`;
}

/**
 * Per-route SEO pro veřejné stránky SPA: title, meta description, canonical
 * a og:* se aktualizují při navigaci. Google JS renderuje, takže tohle stačí;
 * statické defaulty v index.html kryjí scrapery bez JS (FB/LinkedIn).
 *
 * Volat na začátku page komponenty (unconditional — hodnoty smí být undefined,
 * dokud se data načítají; do té doby platí výchozí brand meta).
 */
export function usePageMeta({ title, description, path, noindex }: PageMeta): void {
  useEffect(() => {
    const fullTitle = title ? `${title} — ${SITE_NAME}` : DEFAULT_TITLE;
    const desc = description ? truncate(description) : DEFAULT_DESCRIPTION;

    document.title = fullTitle;
    upsertMeta("name", "description", desc);
    upsertMeta("property", "og:title", fullTitle);
    upsertMeta("property", "og:description", desc);

    // noindex a canonical se vylučují (Google je nekombinuje) — chybová
    // stránka dostane noindex a canonical se odstraní.
    const robotsEl = document.head.querySelector('meta[name="robots"]');
    const canonicalEl = document.head.querySelector('link[rel="canonical"]');
    if (noindex) {
      upsertMeta("name", "robots", "noindex");
      canonicalEl?.remove();
      return;
    }
    robotsEl?.remove();

    if (path) {
      const url = `${CANONICAL_BASE}${path}`;
      upsertCanonical(url);
      upsertMeta("property", "og:url", url);
    }
  }, [title, description, path, noindex]);
}
